import { Box, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControl, IconButton, InputLabel, MenuItem, Select, styled } from '@mui/material';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react'
import { apiService } from '../../../api/apiService';
import { useMsal } from '@azure/msal-react';
import { PublicClientApplication } from '@azure/msal-browser';
import ProcessMapUploadForm from '../Forms/ProcessMapUploadForm/ProcessMapUploadForm';
import SuccessPopup from '../Popup/Success&ErrorPopup/SuccessPopup';
import ErrorPopup from '../Popup/Success&ErrorPopup/ErrorPopup';

interface ResultUploadPopupProps {
    open: boolean;
    onClose: () => void;
    fileId?: string | number;
    initialYear?: string;
    initialHalfYear?: string;
    isUpdate?: boolean;
    onUploadSuccess?: () => void;
}

const StyledMenuItem = styled(MenuItem)({
    color: 'black',
    '&:hover': {
        backgroundColor: '#f0f0f0',
    },
});

const ResultUploadPopup: React.FC<ResultUploadPopupProps> = ({ open, onClose, fileId, initialYear, initialHalfYear, isUpdate, onUploadSuccess }) => {
    const { instance } = useMsal();
    const [selectedYear, setSelectedYear] = useState<string>(
        typeof initialYear === 'string' && initialYear.trim() !== ''
            ? initialYear
            : new Date().getFullYear().toString()
    );

    const [selectedHalfYearly, setSelectedHalfYearly] = useState<string>(
        typeof initialHalfYear === 'string' && initialHalfYear.trim() !== ''
            ? initialHalfYear
            : "H1"
    );

    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [uploadProgress, setUploadProgress] = useState(0);
    const [showSuccess, setShowSuccess] = useState(false);
    const [showError, setShowError] = useState(false);
    const [errorMessage, setErrorMessage] = useState('');


    const currentYear = new Date().getFullYear();
    const years = Array.from({ length: currentYear - 2023 + 1 }, (_, i) => (currentYear - i).toString());
    const handleYearChange = (event: any) => {
        setSelectedYear(event.target.value);
    };

    const handleHalfYearlyChange = (event: any) => {
        setSelectedHalfYearly(event.target.value);
    };

    useEffect(() => {
        if (typeof initialYear === 'string' && initialYear.trim() !== '') {
            setSelectedYear(initialYear);
        }
        if (typeof initialHalfYear === 'string' && initialHalfYear.trim() !== '') {
            setSelectedHalfYearly(initialHalfYear);
        }
    }, [initialYear, initialHalfYear]);

    const handleFileSelect = (file: File) => {
        const validExcelTypes = [
            "application/vnd.ms-excel",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        ];
        const fileName = file.name.toLowerCase();
        const validExtension = fileName.endsWith('.xls') ||
            fileName.endsWith('.xlsx') ||
            fileName.endsWith('.ods');

        if (!validExcelTypes.includes(file.type) && !validExtension) {
            setErrorMessage("Please select only Excel file formats (.xls, .xlsx, .ods)");
            setShowError(true);
            return;
        }

        setSelectedFile(file);
    };


    const resetUploadState = () => {
        setSelectedFile(null);
        setUploadProgress(0);
    };

    const handleSubmit = async () => {
        if (!selectedFile) {
            setErrorMessage("Please select a file first");
            setShowError(true);
            return;
        }

        try {
            setIsSubmitting(true);

            const formData = new FormData();
            formData.append("file", selectedFile);
            formData.append(
                "result",
                JSON.stringify({
                    year: selectedYear,
                    half_year: selectedHalfYearly,
                })
            );

            const progressInterval = setInterval(() => {
                setUploadProgress(prev => Math.min(prev + 10, 90));
            }, 300);

            const endpoint = isUpdate && fileId
                ? `/ops/result_file/${fileId}`
                : "/ops/result_file";

            const response = await apiService(
                endpoint,
                "POST",
                formData,
                {
                    "Content-Type": "multipart/form-data"
                },
                instance as PublicClientApplication
            );

            clearInterval(progressInterval);
            setUploadProgress(100);

            if (response.status === "success") {
                setShowSuccess(true);
                if (onUploadSuccess) {
                    onUploadSuccess();
                }
                setTimeout(() => {
                    onClose();
                    resetUploadState();
                }, 1000);
            } else {
                throw new Error(response.message || "Failed to upload file");
            }
        } catch (error) {
            console.error("Error uploading file:", error);
            setErrorMessage(typeof error === 'string' ? error : "Error uploading file. Please try again.");
            setShowError(true);
            setUploadProgress(0);
        } finally {
            setIsSubmitting(false);
        }
    };


    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showSuccess) {
            timer = setTimeout(() => {
                setShowSuccess(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showSuccess]);

    useEffect(() => {
        let timer: NodeJS.Timeout;
        if (showError) {
            timer = setTimeout(() => {
                setShowError(false);
            }, 3000);
        }
        return () => clearTimeout(timer);
    }, [showError]);


    return (
        <>
            <Dialog
                open={open}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', color: '#000', fontFamily: "Avant semibold", fontWeight: "600" }}>
                    {isUpdate ? "Update File" : "Upload File"}

                    <IconButton onClick={onClose}>
                        <X size={24} />
                    </IconButton>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ mt: 2 }}>
                        {isUpdate ? (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel shrink>Year</InputLabel>
                                <Box sx={{
                                    mt: 2,
                                    p: 1.5,
                                    border: '1px solid #c4c4c4',
                                    borderRadius: '8px',
                                    color: '#000',
                                    fontWeight: 'normal'
                                }}>
                                    {selectedYear}
                                </Box>
                            </FormControl>
                        ) : (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Year *</InputLabel>
                                <Select
                                    sx={{ borderRadius: '8px', color: '#000' }}
                                    value={selectedYear}
                                    onChange={handleYearChange}
                                    label="Year *"
                                >
                                    {years.map((year) => (
                                        <StyledMenuItem key={year} value={year}>
                                            {year}
                                        </StyledMenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                        {isUpdate ? (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel shrink>Half Yearly</InputLabel>
                                <Box sx={{
                                    mt: 2,
                                    p: 1.5,
                                    border: '1px solid #c4c4c4',
                                    borderRadius: '8px',
                                    color: '#000',
                                    fontWeight: 'normal'
                                }}>
                                    {selectedHalfYearly}
                                </Box>
                            </FormControl>
                        ) : (
                            <FormControl fullWidth sx={{ mb: 2 }}>
                                <InputLabel>Half Yearly *</InputLabel>
                                <Select
                                    sx={{ borderRadius: '8px', color: '#000' }}
                                    value={selectedHalfYearly}
                                    onChange={handleHalfYearlyChange}
                                    label="Half Yearly *"
                                    displayEmpty
                                >
                                    {["H1", "H2"].map((half) => (
                                        <StyledMenuItem key={half} value={half}>
                                            {half}
                                        </StyledMenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        )}

                    </Box>
                    <ProcessMapUploadForm
                        file={selectedFile}
                        onFileSelect={handleFileSelect}
                        fileTitle="Upload Root cause analysis & action plans for the missed MICS controls defined within 1 month after being raised"
                        onFileRemove={() => {
                            setSelectedFile(null);
                            setUploadProgress(0);
                        }}
                        uploadProgress={uploadProgress}
                        placeholder="Select an Excel file (.xls, .xlsx)"
                    />
                </DialogContent>
                <DialogActions sx={{ p: 2 }}>
                    <Button variant='outlined' onClick={onClose} sx={{ color: '#000', borderColor: '#000' }}>Cancel</Button>
                    <Button
                        variant="contained"
                        disabled={!selectedFile || isSubmitting}
                        onClick={handleSubmit}
                        sx={{
                            color: 'white',
                            bgcolor: 'black',
                            '&:hover': { bgcolor: 'black' },
                            '&:disabled': { bgcolor: 'grey.300' },
                        }}
                    >
                        {isSubmitting ? "Uploading..." : (isUpdate ? "Update" : "Submit")}

                    </Button>
                </DialogActions>
            </Dialog>

            <SuccessPopup
                open={showSuccess}
                onClose={() => setShowSuccess(false)}
                message={isUpdate ? "Result file updated successfully!" : "Result file uploaded successfully!"}
            />
            <ErrorPopup
                open={showError}
                onClose={() => setShowError(false)}
                message={errorMessage}
            />

        </>
    )
}

export default ResultUploadPopup
